// ============================================
// components/ModalHistoricoDispensacoes.jsx
// Modal de Histórico de Dispensações
// Design Glassmorphism Premium
// ============================================

import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { 
  X, 
  Search, 
  Calendar, 
  User,
  Pill,
  FileText,
  Clock,
  ChevronRight,
  Loader2,
  RefreshCw,
  Eye,
  Download,
  Filter,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  History,
  Package,
  Shield
} from "lucide-react";

const ModalHistoricoDispensacoes = ({ 
  aberto, 
  onFechar, 
  dispensacoes = [],
  carregando = false,
  onCarregarMais,
  onDownloadXml,
  onRefresh,
  acabou = false
}) => {
  const [dispensacoesFiltradas, setDispensacoesFiltradas] = useState([]);
  const [buscaPaciente, setBuscaPaciente] = useState("");
  const [buscaData, setBuscaData] = useState("");
  const [dispensacaoSelecionada, setDispensacaoSelecionada] = useState(null);
  const [visualizandoDetalhes, setVisualizandoDetalhes] = useState(false);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [downloadingXml, setDownloadingXml] = useState(null);

  // ✅ Helper para obter nome do paciente (compatível com campos novos e antigos)
  const getPatientName = (dispensacao) => {
    return dispensacao?.patientName || dispensacao?.patient || 'Paciente não informado';
  };

  // ✅ Helper para obter nome do médico (compatível com campos novos e antigos)
  const getDoctorName = (dispensacao) => {
    return dispensacao?.doctorName || dispensacao?.doctor || '';
  };

  // Filtrar dispensações
  useEffect(() => {
    let filtradas = dispensacoes;
    
    // Filtro por nome do paciente
    if (buscaPaciente) {
      filtradas = filtradas.filter(d => {
        const nome = getPatientName(d).toLowerCase();
        return nome.includes(buscaPaciente.toLowerCase());
      });
    }
    
    // Filtro por data
    if (buscaData) {
      filtradas = filtradas.filter(d => {
        const dataDisp = d.dispensedAt ? 
          new Date(d.dispensedAt).toISOString().slice(0, 10) : "";
        return dataDisp.includes(buscaData);
      });
    }
    
    setDispensacoesFiltradas(filtradas);
  }, [dispensacoes, buscaPaciente, buscaData]);

  // Formatar data
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Data não disponível';
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formatar data curta
  const formatDateShort = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR');
  };

  // Carregar mais
  const handleCarregarMais = useCallback(async () => {
    if (carregandoMais || acabou || !onCarregarMais) return;
    setCarregandoMais(true);
    await onCarregarMais();
    setCarregandoMais(false);
  }, [carregandoMais, acabou, onCarregarMais]);

  // Visualizar detalhes
  const handleVisualizarDetalhes = useCallback((dispensacao, event) => {
    if (event?.target?.closest('[data-download-button]')) {
      return;
    }
    setDispensacaoSelecionada(dispensacao);
    setVisualizandoDetalhes(true);
  }, []);

  // ✅ Download XML com feedback visual
  const handleDownload = useCallback(async (dispensacao, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    if (!onDownloadXml || !dispensacao?.prescriptionId) {
      console.error('Download não disponível: falta onDownloadXml ou prescriptionId');
      return;
    }

    const prescriptionId = dispensacao.prescriptionId;
    
    try {
      setDownloadingXml(prescriptionId);
      await onDownloadXml(prescriptionId);
    } catch (error) {
      console.error('Erro ao baixar XML:', error);
    } finally {
      setDownloadingXml(null);
    }
  }, [onDownloadXml]);

  // Fechar modal ao clicar no backdrop
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onFechar();
    }
  };

  if (!aberto) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={handleBackdropClick}
      style={{
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}
    >
      {/* Modal Container */}
      <div 
        className="relative w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] flex flex-col glass-card rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-3 sm:p-4 lg:p-6 flex-shrink-0 border-b border-gray-200/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <div className="p-1.5 sm:p-2 lg:p-3 rounded-lg sm:rounded-xl lg:rounded-2xl bg-emerald-100/80 border border-emerald-200/50 flex-shrink-0">
                <History className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-emerald-700" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-sm sm:text-lg lg:text-2xl sf-pro-display font-semibold text-gray-900 tracking-tight truncate">
                  Histórico de Dispensações
                </h2>
                <p className="text-xs sf-pro-text text-gray-500 mt-0.5 hidden sm:block">
                  Receitas validadas e dispensadas com XML SNGPC
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  disabled={carregando}
                  className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-gray-100/80 hover:bg-gray-200/90 text-gray-600 hover:text-gray-700 rounded-md sm:rounded-lg lg:rounded-xl border border-gray-200/50 hover:border-gray-300/60 transition-all duration-300 flex items-center justify-center shadow-sm hover:shadow-md transform hover:scale-105 flex-shrink-0"
                  title="Atualizar"
                >
                  <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 ${carregando ? 'animate-spin' : ''}`} />
                </button>
              )}
              <button
                onClick={onFechar}
                className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-red-100/80 hover:bg-red-200/90 text-red-600 hover:text-red-700 rounded-md sm:rounded-lg lg:rounded-xl border border-red-200/50 hover:border-red-300/60 transition-all duration-300 flex items-center justify-center shadow-sm hover:shadow-md transform hover:scale-105 flex-shrink-0"
              >
                <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="p-3 sm:p-4 lg:p-6 flex-shrink-0 border-b border-gray-200/50">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
            {/* Busca por paciente */}
            <div className="relative">
              <Search className="absolute left-2.5 sm:left-3 lg:left-4 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por paciente..."
                value={buscaPaciente}
                onChange={(e) => setBuscaPaciente(e.target.value)}
                className="input-glass-mobile w-full pl-8 sm:pl-10 lg:pl-11"
              />
            </div>

            {/* Filtro por data */}
            <div className="relative">
              <Calendar className="absolute left-2.5 sm:left-3 lg:left-4 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
              <input
                type="date"
                value={buscaData}
                onChange={(e) => setBuscaData(e.target.value)}
                className="input-glass-mobile w-full pl-8 sm:pl-10 lg:pl-11"
              />
            </div>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {carregando && dispensacoes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 sm:py-12 lg:py-20">
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-emerald-500 animate-spin mb-2 sm:mb-3 lg:mb-4" />
              <p className="text-gray-600 sf-pro-text text-xs sm:text-sm lg:text-base">Carregando histórico...</p>
            </div>
          ) : dispensacoesFiltradas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 sm:py-12 lg:py-20">
              <div className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 glass-card rounded-full flex items-center justify-center mb-2 sm:mb-3 lg:mb-4">
                <FileText className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-gray-400" />
              </div>
              <h3 className="text-sm sm:text-base lg:text-lg sf-pro-display font-semibold text-gray-700 mb-1 sm:mb-2">
                Nenhuma dispensação encontrada
              </h3>
              <p className="text-xs sf-pro-text text-gray-500 px-4 text-center">
                {buscaPaciente || buscaData 
                  ? "Tente ajustar os filtros de busca"
                  : "As receitas dispensadas aparecerão aqui"
                }
              </p>
            </div>
          ) : (
            <>
              {/* Lista de dispensações */}
              <div className="divide-y divide-gray-200/50">
                {dispensacoesFiltradas.map((dispensacao, index) => (
                  <div
                    key={dispensacao.id || dispensacao.prescriptionId || index}
                    className="p-3 sm:p-4 lg:p-6 hover:bg-white/40 hover:backdrop-blur-lg cursor-pointer group card-hover-instant"
                    onClick={(e) => handleVisualizarDetalhes(dispensacao, e)}
                  >
                    <div className="flex flex-col gap-2 sm:gap-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-12 lg:h-12 bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center text-white font-semibold text-xs sm:text-sm lg:text-base flex-shrink-0 shadow-sm">
                            {getPatientName(dispensacao).charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 sf-pro-text text-xs sm:text-sm lg:text-base truncate">
                              {getPatientName(dispensacao)}
                            </p>
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 lg:gap-3 text-xs text-gray-600 mt-0.5 sm:mt-1 lg:mt-1.5">
                              <span className="flex items-center space-x-1">
                                <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                <span className="sf-pro-text">{formatDateShort(dispensacao.dispensedAt)}</span>
                              </span>
                              <span className="glass-badge-mobile bg-emerald-100/70 text-emerald-700 border-emerald-200/50 flex items-center space-x-0.5 sm:space-x-1">
                                <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                <span className="sf-pro-text font-medium">Dispensada</span>
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Botão de download */}
                        <button
                          data-download-button
                          onClick={(e) => handleDownload(dispensacao, e)}
                          disabled={downloadingXml === dispensacao.prescriptionId}
                          className="px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-md sm:rounded-lg lg:rounded-xl font-medium text-xs flex items-center space-x-1 transition-all duration-200 flex-shrink-0 bg-gray-900 hover:bg-gray-800 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Baixar XML SNGPC"
                        >
                          {downloadingXml === dispensacao.prescriptionId ? (
                            <Loader2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 animate-spin" />
                          ) : (
                            <Download className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          )}
                          <span className="hidden sm:inline sf-pro-text">XML</span>
                        </button>
                      </div>

                      {/* Preview */}
                      <div className="ml-10 sm:ml-12 lg:ml-16 text-xs text-gray-600">
                        <p className="sf-pro-text">
                          {dispensacao.itemsCount || dispensacao.items?.length || 0} medicamento(s) dispensado(s)
                          {getDoctorName(dispensacao) && ` • Dr(a). ${getDoctorName(dispensacao)}`}
                        </p>
                      </div>

                      {/* Indicador visual para clique */}
                      <div className="ml-10 sm:ml-12 lg:ml-16 flex items-center text-xs sf-pro-text text-gray-500">
                        <Eye className="w-2.5 h-2.5 mr-1" />
                        <span>Clique para visualizar detalhes</span>
                        <ChevronRight className="w-2.5 h-2.5 ml-1" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Botão carregar mais */}
              {!acabou && onCarregarMais && dispensacoesFiltradas.length > 0 && (
                <div className="p-3 sm:p-4 lg:p-6 text-center border-t border-gray-200/50">
                  <button
                    onClick={handleCarregarMais}
                    disabled={carregandoMais}
                    className="button-glass-mobile sf-pro-text font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {carregandoMais ? (
                      <div className="flex items-center space-x-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Carregando...</span>
                      </div>
                    ) : (
                      "Carregar mais"
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-4 lg:p-6 flex-shrink-0 border-t border-gray-200/50 bg-gray-50/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Shield className="w-3.5 h-3.5" />
              <span className="sf-pro-text">XML SNGPC padrão ANVISA</span>
            </div>
            <span className="text-xs sf-pro-text text-gray-400">
              {dispensacoesFiltradas.length} registro(s)
            </span>
          </div>
        </div>
      </div>

      {/* Modal de detalhes */}
      {visualizandoDetalhes && dispensacaoSelecionada && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4"
          onClick={() => setVisualizandoDetalhes(false)}
          style={{
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <div 
            className="relative w-full max-w-2xl sm:max-w-3xl max-h-[90vh] sm:max-h-[85vh] flex flex-col glass-card rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header detalhes */}
            <div className="p-3 sm:p-4 lg:p-6 flex-shrink-0 border-b border-gray-200/50">
              <div className="flex items-center justify-between">
                <h3 className="text-sm sm:text-lg lg:text-xl sf-pro-display font-semibold text-gray-900 tracking-tight">
                  Detalhes da Dispensação
                </h3>
                <button
                  onClick={() => setVisualizandoDetalhes(false)}
                  className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-red-100/80 hover:bg-red-200/90 text-red-600 hover:text-red-700 rounded-md sm:rounded-lg lg:rounded-xl border border-red-200/50 hover:border-red-300/60 transition-all duration-300 flex items-center justify-center shadow-sm hover:shadow-md transform hover:scale-105"
                >
                  <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                </button>
              </div>
            </div>

            {/* Conteúdo detalhes */}
            <div className="flex-1 p-3 sm:p-4 lg:p-6 overflow-y-auto custom-scrollbar space-y-3 sm:space-y-4 lg:space-y-6">
              {/* Status */}
              <div className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-emerald-50/80 border border-emerald-200/50">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="sf-pro-text font-semibold text-emerald-800 text-sm">
                    Dispensação Concluída
                  </p>
                  <p className="sf-pro-text text-emerald-700 text-xs">
                    {formatDate(dispensacaoSelecionada.dispensedAt)}
                  </p>
                </div>
              </div>

              {/* Informações do paciente */}
              <div>
                <h4 className="sf-pro-display font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm lg:text-base">
                  <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                  <span>Paciente</span>
                </h4>
                <div className="glass-card rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-5 space-y-1.5 sm:space-y-2 lg:space-y-3">
                  <p className="sf-pro-text text-xs sm:text-sm lg:text-base">
                    <span className="text-gray-600">Nome:</span>{" "}
                    <span className="font-medium text-gray-900">
                      {getPatientName(dispensacaoSelecionada)}
                    </span>
                  </p>
                  {dispensacaoSelecionada.patientCpf && (
                    <p className="sf-pro-text text-xs sm:text-sm lg:text-base">
                      <span className="text-gray-600">CPF:</span>{" "}
                      <span className="font-medium text-gray-900">
                        {dispensacaoSelecionada.patientCpf}
                      </span>
                    </p>
                  )}
                </div>
              </div>

              {/* Informações do prescritor */}
              {getDoctorName(dispensacaoSelecionada) && (
                <div>
                  <h4 className="sf-pro-display font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm lg:text-base">
                    <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                    <span>Prescritor</span>
                  </h4>
                  <div className="glass-card rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-5 space-y-1.5 sm:space-y-2 lg:space-y-3">
                    <p className="sf-pro-text text-xs sm:text-sm lg:text-base">
                      <span className="text-gray-600">Nome:</span>{" "}
                      <span className="font-medium text-gray-900">
                        {getDoctorName(dispensacaoSelecionada)}
                      </span>
                    </p>
                    {dispensacaoSelecionada.doctorCrm && (
                      <p className="sf-pro-text text-xs sm:text-sm lg:text-base">
                        <span className="text-gray-600">CRM:</span>{" "}
                        <span className="font-medium text-gray-900">
                          {dispensacaoSelecionada.doctorCrm}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Medicamentos dispensados */}
              {dispensacaoSelecionada.items && dispensacaoSelecionada.items.length > 0 && (
                <div>
                  <h4 className="sf-pro-display font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm lg:text-base">
                    <Pill className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                    <span>Medicamentos Dispensados</span>
                  </h4>
                  <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                    {dispensacaoSelecionada.items.map((item, index) => (
                      <div key={index} className="glass-card rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-5">
                        <p className="font-semibold text-gray-900 sf-pro-text mb-2 text-xs sm:text-sm lg:text-base">
                          {item.productName || item.name || item.nome}
                        </p>
                        <div className="grid grid-cols-2 gap-1.5 sm:gap-2 lg:gap-3 text-xs sf-pro-text">
                          <p>
                            <span className="text-gray-600">Quantidade:</span>{" "}
                            <span className="font-medium text-gray-900">{item.quantity || item.quantidade}</span>
                          </p>
                          {item.batch && (
                            <p>
                              <span className="text-gray-600">Lote:</span>{" "}
                              <span className="font-medium text-gray-900">{item.batch}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ID da Prescrição */}
              {dispensacaoSelecionada.prescriptionId && (
                <div className="glass-card rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-5">
                  <p className="sf-pro-text text-xs">
                    <span className="text-gray-600">ID da Receita:</span>{" "}
                    <span className="font-mono font-medium text-gray-900 text-xs">
                      {dispensacaoSelecionada.prescriptionId}
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Footer com botão de download */}
            <div className="p-3 sm:p-4 lg:p-6 flex-shrink-0 border-t border-gray-200/50">
              <button
                onClick={() => {
                  handleDownload(dispensacaoSelecionada);
                  setVisualizandoDetalhes(false);
                }}
                disabled={downloadingXml === dispensacaoSelecionada?.prescriptionId}
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 lg:py-3 rounded-lg sm:rounded-xl font-medium flex items-center justify-center space-x-1 sm:space-x-1.5 lg:space-x-2 transition-all duration-200 text-xs sm:text-sm bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingXml === dispensacaoSelecionada?.prescriptionId ? (
                  <>
                    <Loader2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 animate-spin" />
                    <span className="sf-pro-text font-semibold">Baixando...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4" />
                    <span className="sf-pro-text font-semibold">Baixar XML SNGPC</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estilos */}
      <style>{`
        .sf-pro-display {
          font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        .sf-pro-text {
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.6);
        }

        .card-hover-instant {
          transition: none !important;
        }

        .card-hover-instant:hover {
          background: rgba(255, 255, 255, 0.4) !important;
          backdrop-filter: blur(16px) !important;
          -webkit-backdrop-filter: blur(16px) !important;
        }

        .input-glass-mobile {
          padding: 10px 14px;
          background: rgba(255, 255, 255, 0.7);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 8px;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif;
          color: #1f2937;
          font-size: 14px;
          box-shadow: 
            0 1px 3px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.6);
        }

        .input-glass-mobile::placeholder {
          color: #9ca3af;
          font-size: 14px;
        }

        .input-glass-mobile:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.9);
          border-color: rgba(16, 185, 129, 0.5);
          box-shadow: 
            0 0 0 3px rgba(16, 185, 129, 0.1),
            0 4px 6px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
          transform: translateY(-1px);
        }

        .button-glass-mobile {
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.7);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 8px;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif;
          font-size: 12px;
          font-weight: 500;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          box-shadow: 
            0 1px 3px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.6);
        }

        .button-glass-mobile:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-1px);
          box-shadow: 
            0 4px 12px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .glass-badge-mobile {
          padding: 2px 6px;
          border-radius: 6px;
          border: 1px solid;
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif;
          font-size: 10px;
          font-weight: 500;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          box-shadow: 
            0 1px 3px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.6);
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(241, 245, 249, 0.5);
          border-radius: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(203, 213, 225, 0.8);
          border-radius: 4px;
          transition: background 0.2s ease;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.9);
        }

        @media (max-width: 640px) {
          .custom-scrollbar::-webkit-scrollbar {
            width: 2px;
          }

          .custom-scrollbar {
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
          }

          .input-glass-mobile {
            font-size: 16px !important;
            padding: 12px 16px;
          }

          .input-glass-mobile::placeholder {
            font-size: 16px !important;
          }

          .glass-card {
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
          }

          button {
            min-height: 44px;
            min-width: 44px;
          }
        }

        @media (min-width: 640px) and (max-width: 1024px) {
          .input-glass-mobile {
            padding: 12px 16px;
            border-radius: 10px;
          }

          .button-glass-mobile {
            padding: 10px 16px;
            border-radius: 10px;
            font-size: 14px;
          }
        }

        @media (min-width: 1024px) {
          .input-glass-mobile {
            padding: 14px 18px;
            border-radius: 12px;
            font-size: 16px;
          }

          .input-glass-mobile::placeholder {
            font-size: 16px;
          }

          .button-glass-mobile {
            padding: 12px 18px;
            border-radius: 12px;
            font-size: 14px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        [data-download-button] {
          user-select: none;
          -webkit-user-select: none;
        }

        @media (pointer: coarse) {
          button {
            min-height: 44px;
          }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default ModalHistoricoDispensacoes;